import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faXmark } from '@fortawesome/free-solid-svg-icons';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router-dom';

import { getZonesLOV, type ZoneSummary } from 'api/zones/endpoints/getZonesLOV';
import { parseInt32 } from 'helpers/int32';

import { LiveEventCard } from './LiveEventCard';
import { LiveEventsModel } from './LiveEventsModel';
import { SourcesModal } from './SourcesModal';
import { SurplusModal } from './SurplusModal';
import './LiveEvents.scss';

import { notify } from 'messages';
import { toError } from 'helpers/toError';

/**
 * Live Events: the events whose supply and demand are out of balance.
 *
 * An empty page means nothing needs attention, which is why balanced and idle
 * events are omitted rather than shown with zeroes.
 *
 * `focus` is a URL parameter rather than another header filter: it force-shows
 * one event's card whether or not it qualifies, pinned to the top, without
 * disturbing the operator's own filter state. Arriving from the Events list's
 * "Show live" action, a balanced event still gets a card reading surplus 0 /
 * demand 0 — which answers "why is nothing happening?", where an empty page
 * would not.
 */
const LiveEvents = observer(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const model = useMemo(() => new LiveEventsModel(), []);
  const [zones, setZones] = useState<readonly ZoneSummary[]>([]);
  const [surplusFor, setSurplusFor] = useState<number | null>(null);
  const [sourcesFor, setSourcesFor] = useState<number | null>(null);

  useEffect(() => () => model.dispose(), [model]);

  // The URL owns focus, so a reload or a shared link lands on the same card and
  // the browser's back button clears it.
  //
  // Anything the backend would reject is treated as no focus at all, because a
  // rejected event_ids makes the whole page error rather than costing one card:
  // `?focus=` alone gave Number('') === 0, which is an integer, and 0 fails the
  // route's `id >= 1`. parseInt32 also bounds the value to the int4 column, so
  // an id above 2147483647 cannot 400 the page either. Only a hand-edited URL
  // reaches any of this — setFocus deletes the parameter rather than emptying
  // it — but the cost of getting it wrong is the entire page.
  const focusParam = searchParams.get('focus');
  useEffect(() => {
    const parsed = focusParam === null ? null : parseInt32(focusParam);
    model.setFocusEventId(parsed !== null && parsed > 0 ? parsed : null);
  }, [focusParam, model]);

  useEffect(() => {
    getZonesLOV()
      .then(setZones)
      .catch((err) => {
        // An empty dropdown and an unfilterable one look identical, so say which
        // this is — the filter is how you find anything on a busy page. The
        // contract refuses a partial list (a zone missing from it is one the
        // operator cannot filter to); this is where that becomes visible.
        setZones([]);
        notify.error(`Zone filter unavailable: ${toError(err).message}`, { key: 'live-events-zones' });
      });
  }, []);

  // Focus lives in the URL, so pinning and unpinning are the same edit from
  // either side: the chip's cross, or a card's pin. The model follows the URL,
  // never the other way round, so there is one source of truth.
  const setFocus = (eventId: number | null) => {
    const next = new URLSearchParams(searchParams);
    if (eventId === null) {
      next.delete('focus');
    } else {
      next.set('focus', String(eventId));
    }
    setSearchParams(next, { replace: true });
  };

  const focusedName = model.focusEventId === null ? null : (model.cards.find((card) => card.focused)?.name ?? null);

  return (
    <div className="live-events">
      <div className="live-events__header">
        <h4 className="mb-0">Live Events</h4>

        <Form.Select
          className="live-events__zone"
          aria-label="Zone"
          value={model.zoneId ?? ''}
          onChange={(event) => model.setZoneId(event.target.value === '' ? null : Number(event.target.value))}
        >
          <option value="">All zones</option>
          {zones.map((zone) => (
            <option key={zone.zone_id} value={zone.zone_id}>
              {zone.name}
            </option>
          ))}
        </Form.Select>

        <Form.Control
          className="live-events__search"
          type="search"
          placeholder="Event, process or instance id"
          aria-label="Search"
          value={model.search}
          onChange={(event) => model.setSearch(event.target.value)}
        />

        <Form.Check
          type="switch"
          id="live-events-auto-submit"
          className="live-events__toggle"
          label="Show auto-submit as demand"
          checked={model.includeAutoSubmit}
          onChange={(event) => model.setIncludeAutoSubmit(event.target.checked)}
        />

        <div className="live-events__header-actions">
          <Button variant="link" className="live-events__link" onClick={model.expandAll}>
            Expand all
          </Button>
          <Button variant="link" className="live-events__link" onClick={model.collapseAll}>
            Collapse all
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => void model.refreshPage()} title="Reload the page">
            <FontAwesomeIcon icon={faArrowsRotate} />
          </Button>
        </div>
      </div>

      {model.focusEventId !== null && (
        <Badge bg="secondary" className="live-events__focus-chip">
          Focused: {focusedName ?? model.focusEventId}
          <Button variant="link" size="sm" className="live-events__focus-clear" aria-label="Clear focus" onClick={() => setFocus(null)}>
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </Badge>
      )}

      {model.pageError !== null && <Alert variant="danger">{model.pageError}</Alert>}

      {model.truncated && <Alert variant="warning">Showing the first {model.cards.length} events. There is more out of balance than this page can show.</Alert>}

      {model.isLoading && <Spinner animation="border" size="sm" />}

      {!model.isLoading && model.cards.length === 0 && model.pageError === null && (
        <p className="text-muted">Nothing is out of balance. Every event has as much supply as demand.</p>
      )}

      {/* Dimmed rather than blanked while a newer answer is on its way, so the
          page never flickers empty between filter changes. */}
      <div className={model.isStale ? 'live-events__cards live-events__cards--stale' : 'live-events__cards'}>
        {model.cards.map((card) => (
          <LiveEventCard
            key={card.eventId}
            card={card}
            onShowSurplus={() => setSurplusFor(card.eventId)}
            onShowSources={() => setSourcesFor(card.eventId)}
            onTogglePin={() => setFocus(card.focused ? null : card.eventId)}
          />
        ))}
      </div>

      {surplusFor !== null && (
        <SurplusModal
          eventId={surplusFor}
          onHide={() => setSurplusFor(null)}
          onDiscarded={() => void model.cards.find((card) => card.eventId === surplusFor)?.refresh()}
        />
      )}
      {sourcesFor !== null && <SourcesModal eventId={sourcesFor} onHide={() => setSourcesFor(null)} />}
    </div>
  );
});

export default LiveEvents;
